import React, { useState, useEffect } from "react";
import {NavLink} from "react-router-dom";
import SvgConsole from "../../../svg/SvgConsole";
import {InnerConsole} from "../console/Console";
import PlayerList from "../players/PlayerList";
import {
    LucideChartLine,
    LucideMessageCircle,
    LucidePersonStanding,
    LucideSettings,
    LucideX,
    LucideGripVertical
} from "lucide-react";
import {ChatWindow} from "../chat/Chat";
import {StatsType, StatsWidget} from "../stats/Stats";
import {Chart} from "chart.js";
import {useUserSettings} from "../settings/UserSettingsContext";

export enum WidgetType {
    SERVER_STATS = "server_stats",
    CONSOLE = "console",
    ONLINE_PLAYERS = "online_players",
    CHAT = "chat"
}

// This is what gets saved to the backend
export interface WidgetSettings {
    id: string;
    visible: boolean;
    position: number;
}

export interface DashboardSettings {
    widgets: WidgetSettings[];
}

// Frontend widget definitions - never saved to backend
interface WidgetDefinition {
    id: string;
    type: WidgetType;
    title: string;
    navTo?: string;
    icon: React.FC<{ size: string | number | undefined }>;
    renderContent: () => React.ReactNode;
}

// Complete widget info combining settings and definitions
interface WidgetInfo extends WidgetDefinition, WidgetSettings {}

// Frontend widget definitions
const widgetDefinitions: WidgetDefinition[] = [
    {
        id: "server_stats",
        type: WidgetType.SERVER_STATS,
        title: "Server Stats",
        navTo: "../stats",
        icon: LucideChartLine,
        renderContent: () => <StatsWidget type={StatsType.PLAYER_COUNT} />
    },
    {
        id: "console",
        type: WidgetType.CONSOLE,
        title: "Console",
        navTo: "../console",
        icon: SvgConsole,
        renderContent: () => <InnerConsole />
    },
    {
        id: "online_players",
        type: WidgetType.ONLINE_PLAYERS,
        title: "Online Players",
        navTo: "../players",
        icon: LucidePersonStanding,
        renderContent: () => <PlayerList />
    },
    {
        id: "chat",
        type: WidgetType.CHAT,
        title: "Chat",
        navTo: "../chat",
        icon: LucideMessageCircle,
        renderContent: () => <ChatWindow hideInput={true} />
    }
];

// Default settings for new users or reset
const defaultDashboardSettings: DashboardSettings = {
    widgets: [
        { id: "server_stats", visible: true, position: 0 },
        { id: "console", visible: true, position: 1 },
        { id: "online_players", visible: true, position: 2 },
        { id: "chat", visible: true, position: 3 }
    ]
};

export default function Dashboard() {
    Chart.defaults.color = '#fff';
    
    const userSettings = useUserSettings();
    
    // Local dashboard state
    const [dashboardSettings, setDashboardSettings] = useState<DashboardSettings>(defaultDashboardSettings);
    const [isConfigMode, setIsConfigMode] = useState(false);
    const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Load dashboard settings from user settings when they change
    useEffect(() => {
        if (userSettings.settings.dashboardLayoutJSON) {
            try {
                const loadedSettings = JSON.parse(userSettings.settings.dashboardLayoutJSON) as DashboardSettings;
                const validatedSettings = validateAndMergeSettings(loadedSettings);
                setDashboardSettings(validatedSettings);
            } catch (parseError) {
                console.warn('Failed to parse dashboard settings JSON, using defaults:', parseError);
                setDashboardSettings(defaultDashboardSettings);
            }
        } else {
            setDashboardSettings(defaultDashboardSettings);
        }
    }, [userSettings.settings.dashboardLayoutJSON]);

    // Combine settings with definitions to create complete widget info
    const combineWidgetInfo = (): WidgetInfo[] => {
        return dashboardSettings.widgets
            .map(settings => {
                const definition = widgetDefinitions.find(def => def.id === settings.id);
                if (!definition) {
                    console.warn(`Widget definition not found for id: ${settings.id}`);
                    return null;
                }
                return { ...definition, ...settings };
            })
            .filter(Boolean) as WidgetInfo[];
    };

    const visibleWidgets = combineWidgetInfo()
        .filter(w => w.visible)
        .sort((a, b) => a.position - b.position);

    const allWidgets = combineWidgetInfo()
        .sort((a, b) => a.position - b.position);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, widgetId: string) => {
        setDraggedWidget(widgetId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetWidgetId: string) => {
        e.preventDefault();
        
        if (!draggedWidget || draggedWidget === targetWidgetId) {
            setDraggedWidget(null);
            return;
        }

        const newSettings = { ...dashboardSettings };
        const draggedIndex = newSettings.widgets.findIndex(w => w.id === draggedWidget);
        const targetIndex = newSettings.widgets.findIndex(w => w.id === targetWidgetId);
        
        if (draggedIndex !== -1 && targetIndex !== -1) {
            // Swap positions
            const draggedPosition = newSettings.widgets[draggedIndex].position;
            const targetPosition = newSettings.widgets[targetIndex].position;
            
            newSettings.widgets[draggedIndex].position = targetPosition;
            newSettings.widgets[targetIndex].position = draggedPosition;
        }
        
        setDashboardSettings(newSettings);
        setDraggedWidget(null);
    };

    const toggleWidgetVisibility = (widgetId: string) => {
        const newSettings = { ...dashboardSettings };
        const widget = newSettings.widgets.find(w => w.id === widgetId);
        if (widget) {
            widget.visible = !widget.visible;
            setDashboardSettings(newSettings);
        }
    };

    // Auto-save when leaving config mode
    const handleConfigModeToggle = async () => {
        if (isConfigMode) {
            // Leaving config mode - save changes
            setIsSaving(true);
            try {
                const dashboardLayoutJson = JSON.stringify(dashboardSettings);
                await userSettings.update(dashboardLayoutJson, undefined);
                console.log('Dashboard settings saved successfully');
            } catch (error) {
                console.error('Failed to save dashboard settings:', error);
            } finally {
                setIsSaving(false);
            }
        }
        setIsConfigMode(!isConfigMode);
    };

    // Validate loaded settings and merge with defaults for any missing widgets
    const validateAndMergeSettings = (loadedSettings: DashboardSettings): DashboardSettings => {
        const defaultWidgetIds = defaultDashboardSettings.widgets.map(w => w.id);
        const loadedWidgetIds = loadedSettings.widgets.map(w => w.id);
        
        // Add any missing widgets from defaults
        const missingWidgets = defaultDashboardSettings.widgets.filter(
            defaultWidget => !loadedWidgetIds.includes(defaultWidget.id)
        );
        
        // Remove any widgets that no longer exist in definitions
        const validWidgets = loadedSettings.widgets.filter(
            widget => widgetDefinitions.some(def => def.id === widget.id)
        );
        
        return {
            widgets: [...validWidgets, ...missingWidgets].sort((a, b) => a.position - b.position)
        };
    };

    return (
        <div className="h-full flex flex-col p-4 overflow-hidden">
            {/* Header - Fixed height */}
            <div className="mb-4 flex justify-between items-center flex-shrink-0">
                <h1 className="text-2xl font-bold text-white">Dashboard</h1>
                <button
                    onClick={handleConfigModeToggle}
                    disabled={isSaving}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                        isConfigMode || isSaving
                            ? 'bg-primary hover:bg-primary-hover text-white'
                            : 'hover:bg-neutral-700 text-gray-400 hover:text-gray-300'
                    }`}
                >
                    <LucideSettings size={16} />
                    {isSaving ? 'Saving...' : (isConfigMode ? 'Done' : 'Customize')}
                </button>
            </div>

            {/* Main content area - takes remaining height */}
            <div className="flex-1 min-h-0 flex flex-col">
                {/* Widget Selection Panel - Fixed height when visible */}
                {isConfigMode && (
                    <div className="mb-4 p-4 bg-neutral-800 rounded-md flex-shrink-0">
                        <h3 className="text-white font-bold mb-3">Widget Settings</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {allWidgets.map(widget => (
                                <label key={widget.id} className="flex items-center gap-2 text-white cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={widget.visible}
                                        onChange={() => toggleWidgetVisibility(widget.id)}
                                        className="form-checkbox h-4 w-4 text-primary"
                                    />
                                    <widget.icon size={16} />
                                    <span className="text-sm">{widget.title}</span>
                                </label>
                            ))}
                        </div>
                        <p className="text-neutral-400 text-sm mt-2">
                            Drag widgets to reorder them. Check/uncheck to show/hide widgets. Changes will be saved when you click "Done".
                        </p>
                    </div>
                )}

                {/* Widget grid - takes remaining space after config panel */}
                <div className={`grid gap-4 flex-1 min-h-0 overflow-y-auto lg:overflow-hidden ${
                    visibleWidgets.length === 1 ? 'grid-cols-1' :
                    visibleWidgets.length === 2 ? 'grid-cols-1 lg:grid-cols-2' :
                    visibleWidgets.length === 3 ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' :
                    'grid-cols-1 lg:grid-cols-2 lg:grid-rows-2'
                }`}>
                    {visibleWidgets.map(widget => (
                        <DraggableWidget
                            key={widget.id}
                            widget={widget}
                            isConfigMode={isConfigMode}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            isDragging={draggedWidget === widget.id}
                        >
                            {widget.renderContent()}
                        </DraggableWidget>
                    ))}
                </div>

                {/* Debug Info - Fixed height when visible */}
                {isConfigMode && (
                    <div className="mt-4 p-2 bg-neutral-900 rounded text-xs text-neutral-400 flex-shrink-0 max-h-40 overflow-y-auto">
                        <details>
                            <summary className="cursor-pointer">Debug: Dashboard Settings</summary>
                            <div className="mt-2 space-y-2">
                                <div>
                                    <strong>Current Settings:</strong>
                                    <pre>{JSON.stringify(dashboardSettings, null, 2)}</pre>
                                </div>
                                <div>
                                    <strong>User Settings JSON:</strong>
                                    <pre>{userSettings.settings.dashboardLayoutJSON || 'null'}</pre>
                                </div>
                            </div>
                        </details>
                    </div>
                )}
            </div>
        </div>
    );
}

function DraggableWidget({
    widget,
    isConfigMode,
    onDragStart,
    onDragOver,
    onDrop,
    isDragging,
    children
}: {
    widget: WidgetInfo;
    isConfigMode: boolean;
    onDragStart: (e: React.DragEvent<HTMLDivElement>, widgetId: string) => void;
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (e: React.DragEvent<HTMLDivElement>, widgetId: string) => void;
    isDragging: boolean;
    children: React.ReactNode;
}) {
    return (
        <div
            draggable={isConfigMode}
            onDragStart={(e) => onDragStart(e, widget.id)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, widget.id)}
            className={`rounded-md bg-neutral-800 flex flex-col min-h-0 transition-all duration-200 ${
                isConfigMode ? 'cursor-move ring-2 ring-primary ring-opacity-50' : ''
            } ${isDragging ? 'opacity-50 scale-95' : ''}`}
        >
            <div className="px-2 py-1 bg-primary rounded-t-md flex flex-row items-center space-x-2 flex-shrink-0">
                {isConfigMode && (
                    <LucideGripVertical size={16} className="text-white opacity-70" />
                )}
                <widget.icon size={18} />
                {widget.navTo ? (
                    <NavLink to={widget.navTo}>
                        <h4 className="font-bold hover:underline">{widget.title}</h4>
                    </NavLink>
                ) : (
                    <h4 className="font-bold">{widget.title}</h4>
                )}
                {isConfigMode && (
                    <div className="flex-grow text-right">
                        <span className="text-xs opacity-70">pos: {widget.position}</span>
                    </div>
                )}
            </div>
            <div className="p-2 flex-1 flex flex-col min-h-0 overflow-hidden">
                {children}
            </div>
        </div>
    );
}